/**
 * Supabase Client Singleton
 * 
 * This is the ONLY createClient call in the frontend app.
 * 
 * ARCHITECTURE NOTE:
 * - All caching, deduplication, circuit breaking, and rate limiting is handled by rpcClient.ts
 * - This module provides ONLY the singleton Supabase client
 * - Trace/observability is handled by fetchTrace.ts (imported before this)
 * 
 * Do NOT add caching or deduplication logic here - use rpcClient.ts functions instead.
 * 
 * DEMO MODE (client-only write guard):
 * When a demo session is active (see useDemoSession), all write operations
 * (insert/update/delete/upsert + auth mutations + mutation RPC prefixes) are
 * blocked at this Proxy layer.
 *
 * RESIDUAL: This guard applies only to calls through this singleton client.
 * Direct REST/RPC/edge calls with a demo player_session token can still mutate
 * data unless blocked server-side. See docs/SECURITY_AND_PRIVACY_REVIEW.md § Demo.
 */
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { isDemoModeActive } from '@/hooks/useDemoSession';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

/**
 * Guard against blank-page deploys from local `vercel build` / `env pull`.
 * Vercel CLI may inline a redacted Sensitive placeholder for encrypted
 * Production env values, which then crashes createClient at module load.
 */
function assertValidSupabaseEnv(url: string | undefined, key: string | undefined): asserts url is string {
  const redacted = (v: string | undefined) =>
    !!v && /^\[?SENSITIVE\]?$/i.test(v.trim());

  const invalid =
    !url ||
    !key ||
    redacted(url) ||
    redacted(key) ||
    !/^https?:\/\//i.test(url);

  if (invalid) {
    throw new Error(
      'Invalid or missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. ' +
        'For local prebuilt deploys, inject real values from .env.local before `vercel build` ' +
        '(Vercel CLI env pull can redact encrypted Production secrets).',
    );
  }
}

assertValidSupabaseEnv(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);

// ============= SINGLETON SUPABASE CLIENT =============

const baseClient = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);

// ============= DEMO MODE WRITE BLOCKER =============
// In demo mode, intercept all writes (insert/update/delete/upsert) to prevent
// any database mutations. Reads pass through unchanged.

let demoToastShownRecently = false;
function notifyDemoBlock(op: string) {
  if (demoToastShownRecently) return;
  demoToastShownRecently = true;
  setTimeout(() => { demoToastShownRecently = false; }, 1500);
  // Lazy-load toast to avoid SSR/import cycles
  import('sonner').then(({ toast }) => {
    toast.error('Demo-tila — muutoksia ei voi tallentaa', {
      description: `Toimintoa "${op}" ei suoriteta demossa.`,
    });
  }).catch(() => {});
}

const blockedError = {
  code: 'DEMO_READ_ONLY',
  message: 'Operation blocked: demo mode is read-only',
  details: null,
  hint: null,
};

function wrapQueryBuilder<T extends object>(qb: T, tableName: string): T {
  const writeOps = new Set(['insert', 'update', 'delete', 'upsert']);
  return new Proxy(qb, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof prop === 'string' && writeOps.has(prop) && typeof value === 'function') {
        return (..._args: unknown[]) => {
          notifyDemoBlock(`${prop} ${tableName}`);
          // Return a thenable that resolves to a Supabase-style error response
          const result = {
            data: null,
            error: blockedError,
            count: null,
            status: 403,
            statusText: 'Forbidden (demo)',
          };
          const thenable: any = {
            ...result,
            select: () => thenable,
            single: () => Promise.resolve(result),
            maybeSingle: () => Promise.resolve(result),
            then: (onFulfilled: any) => Promise.resolve(result).then(onFulfilled),
            catch: (onRejected: any) => Promise.resolve(result).catch(onRejected),
            finally: (onFinally: any) => Promise.resolve(result).finally(onFinally),
          };
          return thenable;
        };
      }
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

function wrapAuth(auth: any) {
  return new Proxy(auth, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      const blocked = new Set([
        'signOut', 'signInWithPassword', 'signInWithOAuth', 'signInWithOtp',
        'signInWithIdToken', 'signUp', 'updateUser', 'resetPasswordForEmail',
        'verifyOtp', 'resend',
      ]);
      if (typeof prop === 'string' && blocked.has(prop) && typeof value === 'function') {
        return async (..._args: unknown[]) => {
          notifyDemoBlock(`auth.${prop}`);
          return { data: { user: null, session: null }, error: blockedError };
        };
      }
      return typeof value === 'function' ? value.bind(target) : value;
    },
  });
}

// Mutation RPCs to block. Read RPCs pass through.
const BLOCKED_RPC_PREFIXES = [
  'create_', 'add_', 'insert_', 'update_', 'set_', 'delete_', 'remove_',
  'archive_', 'restore_', 'issue_', 'reset_', 'rotate_', 'purchase_', 'sell_',
  'award_', 'submit_', 'save_', 'complete_', 'mark_', 'join_', 'leave_',
  'activate_', 'deactivate_', 'redeem_', 'consume_', 'apply_', 'rename_',
  'archive_group', 'handle_', 'track_',
];
// Read RPCs that should NEVER be blocked (allowlist takes precedence over prefix match)
const ALLOWED_READ_RPCS = new Set([
  'validate_demo_code_anon', 'validate_player_session', 'set_player_session_token',
  'get_player_module_progress', 'get_all_tab_completion_status',
  'get_my_school_seat_usage', 'get_group_players_overview', 'get_demo_group_overview',
]);

function shouldBlockRpc(fnName: string): boolean {
  if (ALLOWED_READ_RPCS.has(fnName)) return false;
  if (fnName.startsWith('get_') || fnName.startsWith('validate_') || fnName.startsWith('list_')) {
    return false;
  }
  return BLOCKED_RPC_PREFIXES.some((p) => fnName.startsWith(p));
}

export const supabase = new Proxy(baseClient, {
  get(target, prop, receiver) {
    if (!isDemoModeActive()) {
      const v = Reflect.get(target, prop, receiver);
      return typeof v === 'function' ? v.bind(target) : v;
    }

    // Demo mode active — intercept relevant methods
    if (prop === 'from') {
      return (table: string) => {
        const qb = (target as any).from(table);
        return wrapQueryBuilder(qb, table);
      };
    }
    if (prop === 'rpc') {
      return (fnName: string, args?: any, opts?: any) => {
        if (shouldBlockRpc(fnName)) {
          notifyDemoBlock(`rpc.${fnName}`);
          const result = { data: null, error: blockedError, status: 403, statusText: 'Forbidden (demo)' };
          const thenable: any = {
            ...result,
            single: () => Promise.resolve(result),
            maybeSingle: () => Promise.resolve(result),
            then: (onFulfilled: any) => Promise.resolve(result).then(onFulfilled),
            catch: (onRejected: any) => Promise.resolve(result).catch(onRejected),
            finally: (onFinally: any) => Promise.resolve(result).finally(onFinally),
          };
          return thenable;
        }
        return (target as any).rpc(fnName, args, opts);
      };
    }
    if (prop === 'auth') {
      return wrapAuth((target as any).auth);
    }
    const v = Reflect.get(target, prop, receiver);
    return typeof v === 'function' ? v.bind(target) : v;
  },
}) as typeof baseClient;
